import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface BonusTrackerProps {
  moraleState: 'Mutinous' | 'Resentful' | 'Unnerved' | 'Discouraged' | 'Normal' | 'Heartened';
  cleanlinessState: 'Hazardous' | 'Neglected' | 'Standard' | 'Pristine';
  bonuses?: {
    professionSailor: number;
    balanceAndClimb: number;
    pilot: number;
    allSkillChecks: number;
    shipWeaponAttacks: number;
    nonShipWeaponAttacks: number;
    temporaryHP: number;
    conSavesAgainstPoisonAndDisease: number;
  };
  roleVacancies?: {
    captain: boolean;
    firstMate: boolean;
    pilot: boolean;
  };
  onBonusesChange?: (bonuses: NonNullable<BonusTrackerProps['bonuses']>) => void;
  onRoleVacanciesChange?: (roleVacancies: NonNullable<BonusTrackerProps['roleVacancies']>) => void;
}

const defaultBonuses = {
  professionSailor: 0,
  balanceAndClimb: 0,
  pilot: 0,
  allSkillChecks: 0,
  shipWeaponAttacks: 0,
  nonShipWeaponAttacks: 0,
  temporaryHP: 0,
  conSavesAgainstPoisonAndDisease: 0,
};

const defaultRoleVacancies = {
  captain: false,
  firstMate: false,
  pilot: false,
};

const BonusTracker: React.FC<BonusTrackerProps> = ({ 
  moraleState, 
  cleanlinessState,
  bonuses: externalBonuses,
  roleVacancies: externalRoleVacancies,
  onBonusesChange,
  onRoleVacanciesChange
}) => {
  // Use local state if not provided externally
  const [bonuses, setBonuses] = useState(externalBonuses || defaultBonuses);
  const [roleVacancies, setRoleVacancies] = useState(externalRoleVacancies || defaultRoleVacancies);

  // Update local state when props change
  useEffect(() => {
    if (externalBonuses) {
      setBonuses(externalBonuses);
    }
  }, [externalBonuses]);

  useEffect(() => {
    if (externalRoleVacancies) {
      setRoleVacancies(externalRoleVacancies);
    }
  }, [externalRoleVacancies]);

  const handleBonusChange = (key: keyof typeof bonuses, value: number) => {
    const newBonuses = { ...bonuses, [key]: value };
    setBonuses(newBonuses);
    
    if (onBonusesChange) {
      onBonusesChange(newBonuses);
    }
  };

  const handleRoleVacancyChange = (role: keyof typeof roleVacancies, isVacant: boolean) => {
    const newRoleVacancies = { ...roleVacancies, [role]: isVacant };
    setRoleVacancies(newRoleVacancies);
    
    if (onRoleVacanciesChange) {
      onRoleVacanciesChange(newRoleVacancies);
    }
  };

  const calculateTotalModifier = (baseBonus: number, isAffectedByMorale: boolean = true) => {
    let total = baseBonus;
    
    if (isAffectedByMorale) {
      total += bonuses.allSkillChecks;
      if (moraleState === 'Unnerved' || moraleState === 'Resentful') {
        total -= 2; // Shaken condition
      }
    }
    
    return total;
  };

  const calculateProfessionSailorModifier = () => {
    let total = calculateTotalModifier(bonuses.professionSailor);
    
    if (cleanlinessState === 'Pristine') total += 1;
    else if (cleanlinessState === 'Neglected') total -= 1;
    else if (cleanlinessState === 'Hazardous') total -= 2;
    
    // Apply role vacancy penalties
    if (roleVacancies.captain && roleVacancies.firstMate) total -= 5;
    total -= (Object.values(roleVacancies).filter(Boolean).length * 2);
    
    return total;
  };

  const calculatePilotModifier = () => {
    let total = calculateTotalModifier(bonuses.pilot);
    
    // Apply role vacancy penalties
    if (roleVacancies.pilot) total -= 5;
    if (roleVacancies.captain && roleVacancies.firstMate) total -= 5;
    total -= (Object.values(roleVacancies).filter(Boolean).length * 2);
    
    return total;
  };

  const calculateAllSkillChecksModifier = () => {
    let total = bonuses.allSkillChecks;
    if (moraleState === 'Unnerved' || moraleState === 'Resentful') {
      total -= 2; // Apply shaken condition to All Skill Checks
    }
    return total;
  };

  return (
    <Card className="w-full max-w-2xl mx-auto mt-4">
      <CardHeader>
        <CardTitle>Bonus Tracker</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Skill Checks</h3>
            <div className="space-y-2">
              {[
                { label: 'Profession (Sailor)', key: 'professionSailor', total: calculateProfessionSailorModifier() },
                { label: 'Balance and Climb', key: 'balanceAndClimb', total: calculateTotalModifier(bonuses.balanceAndClimb) },
                { label: 'Pilot', key: 'pilot', total: calculatePilotModifier() },
                { label: 'All Skill Checks', key: 'allSkillChecks', total: calculateAllSkillChecksModifier() },
              ].map(({ label, key, total }) => (
                <div key={key} className="flex items-center space-x-2">
                  <Label htmlFor={key} className="w-40">{label}</Label>
                  <Input
                    id={key}
                    type="number"
                    value={bonuses[key as keyof typeof bonuses]}
                    onChange={(e) => handleBonusChange(key as keyof typeof bonuses, parseInt(e.target.value) || 0)}
                    className="w-20"
                  />
                  <span className="w-20 text-center">Total: {total}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Miscellaneous</h3>
            <div className="space-y-2">
              {[
                { label: 'Ship Weapon Attacks', key: 'shipWeaponAttacks', total: calculateTotalModifier(bonuses.shipWeaponAttacks) },
                { label: 'Non-Ship Weapon Attacks', key: 'nonShipWeaponAttacks', total: calculateTotalModifier(bonuses.nonShipWeaponAttacks) },
                { label: 'Temporary HP', key: 'temporaryHP', total: bonuses.temporaryHP },
                { label: 'Con Saves (Poison/Disease)', key: 'conSavesAgainstPoisonAndDisease', total: calculateTotalModifier(bonuses.conSavesAgainstPoisonAndDisease) },
              ].map(({ label, key, total }) => (
                <div key={key} className="flex items-center space-x-2">
                  <Label htmlFor={key} className="w-40">{label}</Label>
                  <Input
                    id={key}
                    type="number"
                    value={bonuses[key as keyof typeof bonuses]}
                    onChange={(e) => handleBonusChange(key as keyof typeof bonuses, parseInt(e.target.value) || 0)}
                    className="w-20"
                  />
                  <span className="w-20 text-center">Total: {total}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Role Vacancies</h3>
          <div className="space-y-2">
            {Object.entries(roleVacancies).map(([role, isVacant]) => (
              <div key={role} className="flex items-center space-x-2">
                <Checkbox
                  id={role}
                  checked={isVacant}
                  onCheckedChange={(checked) => 
                    handleRoleVacancyChange(role as keyof typeof roleVacancies, checked === true)
                  }
                />
                <Label htmlFor={role} className="capitalize">{role.replace(/([A-Z])/g, ' $1').trim()} Vacant</Label>
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm text-gray-600">
            <p>Captain & First Mate vacant: -5 to Profession (Sailor) and Pilot checks</p>
            <p>Pilot vacant: -5 to Pilot checks</p>
            <p>Each vacant role: -2 to Profession (Sailor) and Pilot checks</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BonusTracker;